import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi, getErrorMessage } from '../services/api';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import axios from 'axios';

export default function BullhornCallbackPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { setUser, setTenant } = useAuth();

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state'); // tenant_slug
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setStatus('error');
        setError(searchParams.get('error_description') || 'Bullhorn login was cancelled');
        return;
      }

      if (!code || !state) {
        setStatus('error');
        setError('Invalid callback parameters');
        return;
      }

      try {
        const response = await authApi.completeBullhornLogin(code, state);
        const { user, tenant } = response.data;

        // Cookie is set automatically by the backend response
        setUser(user);
        setTenant(tenant);
        localStorage.setItem('tenantSlug', tenant.slug);

        setStatus('success');

        // Redirect to dashboard after a brief moment
        setTimeout(() => {
          navigate(`/${tenant.slug}/dashboard`);
        }, 1500);
      } catch (err) {
        setStatus('error');
        setError(getErrorMessage(err, 'Failed to complete Bullhorn login'));
      }
    };

    handleCallback();
  }, [searchParams, navigate, setUser, setTenant]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4 text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-[#0072CE] mx-auto mb-4" />
            <h2 className="font-heading text-xl font-semibold mb-2">Signing you in...</h2>
            <p className="text-muted-foreground">Completing Bullhorn authentication</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-semibold mb-2">Welcome!</h2>
            <p className="text-muted-foreground">Redirecting to your dashboard...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-semibold mb-2">Authentication Failed</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="text-primary hover:underline font-medium"
            >
              Return to login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
